'use client';

import Link from 'next/link';
import SearchNoResults from '@/common/components/search-empty-state/search-no-results.component';
import CardDivider from '@/common/components/card-divider';
import { createQueryStringUtility } from '@/common/utils/create-query-string.utility';
import ChevLeftIcon from '~/public/assets/images/svg/chev-left.svg';
import SearchIcon from '~/public/assets/images/svg/search.svg';
import StoreLineIcon from '~/public/assets/images/svg/store-line.svg';

export interface SearchSuggestionsData {
  categories: Array<{ id: number; title: string }>;
  vendors: Array<{ slug: string; vendorTitle: string }>;
  suggestions: string[];
}

interface SearchSuggestionsListProps {
  value: string;
  data: SearchSuggestionsData;
  onNavigate?: () => void;
  compact?: boolean;
}

export function SearchSuggestionsList({
  value,
  data,
  onNavigate,
  compact = false,
}: SearchSuggestionsListProps) {
  const titleClass = compact
    ? 'text-sm font-medium text-black-700'
    : 'text-xs font-medium text-plp-secondary';
  const itemClass = compact
    ? 'text-sm font-semibold text-black-700'
    : 'text-[13px] font-semibold text-plp-primary';
  const iconWrapClass = compact
    ? 'h-5 w-5 text-black-200'
    : 'h-4 w-4 shrink-0 text-plp-muted';

  const isEmpty =
    data.categories.length === 0 &&
    data.vendors.length === 0 &&
    data.suggestions.length === 0;

  if (isEmpty) {
    return <SearchNoResults variant="products" size="compact" />;
  }

  return (
    <div className="flex flex-col gap-4 bg-white">
      {!!data.categories.length && (
        <section className="flex flex-col gap-3">
          <h3 className={titleClass}>محصولات</h3>
          <div className="flex flex-col gap-4">
            {data.categories.map((category) => (
              <Link
                key={category.id}
                onClick={onNavigate}
                className="flex items-start gap-3"
                href={`/search/products${createQueryStringUtility({
                  queryText: value,
                  categoryId: category.id,
                })}`}
              >
                <div className={iconWrapClass}>
                  <SearchIcon />
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                  <span className={itemClass}>{value}</span>
                  <p className="text-[11px] font-medium text-plp-muted">
                    در دسته بندی {category.title}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!!data.categories.length && !!data.vendors.length && <CardDivider />}

      {!!data.vendors.length && (
        <section className="flex flex-col gap-3">
          <div className="flex w-full items-center justify-between">
            <h3 className={titleClass}>فروشگاه ها</h3>
            <Link
              href={`/search/vendors${createQueryStringUtility({ queryText: value })}`}
              onClick={onNavigate}
              className="flex items-center gap-1 text-[11px] font-semibold text-link"
            >
              مشاهده همه
              <ChevLeftIcon />
            </Link>
          </div>
          <div className="flex flex-col gap-4">
            {data.vendors.map((vendor) => (
              <Link
                key={vendor.slug}
                onClick={onNavigate}
                className="flex items-start gap-3"
                href={`/${vendor.slug}`}
              >
                <div className={iconWrapClass}>
                  <StoreLineIcon />
                </div>
                <span className={itemClass}>{vendor.vendorTitle}</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {!!data.suggestions.length &&
        (!!data.vendors.length || !!data.categories.length) && <CardDivider />}

      {!!data.suggestions.length && (
        <section className="flex flex-col gap-4">
          {data.suggestions.map((suggestion) => (
            <Link
              key={suggestion}
              onClick={onNavigate}
              className="flex items-start gap-3"
              href={`/search/products${createQueryStringUtility({
                queryText: suggestion,
              })}`}
            >
              <div className={iconWrapClass}>
                <SearchIcon />
              </div>
              <span className={itemClass}>{suggestion}</span>
            </Link>
          ))}
        </section>
      )}
    </div>
  );
}
